<template>
  <article class="PurchaseConfirmationCard" :class="isMobile ? 'u-card-bordered' : 'u-card-elevated'">
    <div class="PurchaseConfirmationCard-header">
      <h2 class="PurchaseConfirmationCard-heading">
        <Icon icon="check-circle" size="xl" color="success" />
        {{ heading }}
      </h2>
      <p class="PurchaseConfirmationCard-message">
        {{ message }}
      </p>
    </div>

    <div class="PurchaseConfirmationCard-ctaSection">
      <ActionButton
        size="md"
        variant="primary"
        class="PurchaseConfirmationCard-ctaButton"
        rightIcon="chevron-right"
        @click="handleCtaClick"
      >
        {{ ctaText }}
      </ActionButton>
    </div>

    <div class="PurchaseConfirmationCard-details">
      <div class="PurchaseConfirmationCard-detailRow">
        <span class="PurchaseConfirmationCard-label">{{ planLabel }}:</span>
        <span class="PurchaseConfirmationCard-value">{{ planName }}</span>
      </div>

      <div
        v-if="addOns.length > 0"
        class="PurchaseConfirmationCard-detailRow PurchaseConfirmationCard-detailRow--addOns"
      >
        <span class="PurchaseConfirmationCard-label">{{ addOnsLabel }}:</span>
        <div class="PurchaseConfirmationCard-addOnsList">
          <div v-for="(addOn, index) of addOns" :key="index" class="PurchaseConfirmationCard-addOnItem">
            <span class="PurchaseConfirmationCard-addOnQuantity">({{ addOn.quantity }}x)</span>
            <span class="PurchaseConfirmationCard-addOnName">{{ addOn.name }}</span>
          </div>
        </div>
      </div>

      <div class="PurchaseConfirmationCard-detailRow">
        <span class="PurchaseConfirmationCard-label">{{ billingFrequencyLabel }}:</span>
        <span class="PurchaseConfirmationCard-value">{{ billingFrequency }}</span>
      </div>

      <div class="PurchaseConfirmationCard-detailRow PurchaseConfirmationCard-detailRow--total">
        <span class="PurchaseConfirmationCard-label">{{ totalPriceLabel }}:</span>
        <span class="PurchaseConfirmationCard-value PurchaseConfirmationCard-value--price">
          <s v-if="originalTotalPrice" class="PurchaseConfirmationCard-originalPrice">{{ originalTotalPrice }}</s>
          {{ totalPrice }}
          <Tag
            v-if="originalTotalPrice"
            size="xs"
            type="highlight"
            variant="filled"
            leftIcon="brightness-percent"
            class="PurchaseConfirmationCard-promoTag"
          />
        </span>
      </div>
    </div>

    <div
      v-if="totalPriceChargedLabel && totalPriceCharged"
      class="PurchaseConfirmationCard-detailRow PurchaseConfirmationCard-totalCharged"
    >
      <span class="PurchaseConfirmationCard-totalCharged-label">{{ totalPriceChargedLabel }}:</span>
      <span class="PurchaseConfirmationCard-totalCharged-value">{{ totalPriceCharged }}</span>
    </div>

    <p v-if="promoFootnote" class="PurchaseConfirmationCard-footnote">
      {{ promoFootnote }}
    </p>
  </article>
</template>

<script setup lang="ts">
import { useResponsive } from '@cvo/ui/composables';
import ActionButton from '../../atoms/ActionButton/ActionButton.vue';
import Icon from '../../atoms/Icon/Icon.vue';
import Tag from '../../atoms/Tag/Tag.vue';
import type { PurchaseConfirmationCardProps } from './PurchaseConfirmationCard.model';

const props = defineProps<PurchaseConfirmationCardProps>();
const emit = defineEmits<{
  (e: 'cta-click'): void;
}>();

const { isMobile } = useResponsive();

function handleCtaClick() {
  emit('cta-click');
}
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/text';
@use '@cvo/ui/styles/utils/responsive';

@layer cvo-ui {
  .PurchaseConfirmationCard {
    width: 100%;
    padding: vars.$dimensions-d-6;

    @include responsive.mobile {
      padding: vars.$dimensions-d-5;
    }
  }

  .PurchaseConfirmationCard-header {
    text-align: center;
    margin-bottom: vars.$dimensions-d-6;
    display: flex;
    flex-direction: column;
    gap: vars.$dimensions-d-4;
  }

  .PurchaseConfirmationCard-heading {
    color: vars.$organisms-modal-title-color;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: vars.$dimensions-d-4;
    @include text.heading-responsive('h4', 'h3');

    @include responsive.mobile {
      flex-direction: column;
    }
  }

  .PurchaseConfirmationCard-message {
    color: vars.$organisms-modal-text-color;
    @include text.text(md);

    @include responsive.mobile {
      @include text.text(sm);
    }
  }

  .PurchaseConfirmationCard-ctaSection {
    margin-bottom: vars.$dimensions-d-6;
    text-align: center;
  }

  .PurchaseConfirmationCard-ctaButton {
    min-width: 200px;

    @include responsive.mobile {
      width: 100%;
    }
  }

  .PurchaseConfirmationCard-details {
    display: flex;
    flex-direction: column;
    gap: vars.$dimensions-d-3;
    padding-top: vars.$dimensions-d-4;
    border-top: vars.$organisms-modal-contentmodal-border-stroke solid vars.$organisms-modal-contentmodal-border-color;
  }

  .PurchaseConfirmationCard-detailRow {
    display: flex;
    align-items: flex-start;
    gap: vars.$dimensions-d-2;

    @include responsive.mobile {
      gap: vars.$dimensions-d-1;
      flex-direction: column;
    }

    &--addOns {
      flex-direction: column;
    }
  }

  .PurchaseConfirmationCard-label {
    color: vars.$organisms-modal-contentmodal-label-color;
    @include text.text(md, vars.$typography-weight-strong);

    @include responsive.mobile {
      color: vars.$organisms-modal-contentmodal-label-color;
      @include text.text(sm, vars.$typography-weight-strong);
    }
  }

  .PurchaseConfirmationCard-value {
    color: vars.$organisms-modal-contentmodal-text-color;
    @include text.text(md);

    @include responsive.mobile {
      color: vars.$organisms-modal-contentmodal-text-color;
      @include text.text(sm);
    }

    &--price {
      display: inline-flex;
      align-items: center;
      gap: vars.$dimensions-d-1;
    }
  }

  .PurchaseConfirmationCard-originalPrice {
    color: vars.$molecules-breakdown-secondary-text-color;
    font-weight: vars.$typography-weight-body;
  }

  .PurchaseConfirmationCard-promoTag {
    padding: vars.$dimensions-d-1;
    min-width: auto;
  }

  .PurchaseConfirmationCard-totalCharged {
    margin-top: vars.$dimensions-d-1;

    &-label,
    &-value {
      color: vars.$molecules-breakdown-secondary-text-color;
      @include text.text(xs, vars.$typography-weight-body);
    }
  }

  .PurchaseConfirmationCard-footnote {
    margin-block-start: vars.$dimensions-d-4;
    color: vars.$molecules-breakdown-secondary-text-color;
    @include text.text(xs, vars.$typography-weight-body);
  }

  .PurchaseConfirmationCard-addOnsList {
    display: flex;
    flex-direction: column;
    gap: vars.$dimensions-d-1;
    flex: 1;
  }

  .PurchaseConfirmationCard-addOnItem {
    display: flex;
    gap: vars.$dimensions-d-1;
    color: vars.$molecules-breakdown-secondary-text-color;
    @include text.text(md);

    @include responsive.mobile {
      @include text.text(sm);
    }
  }
}
</style>
